import { MouseEvent, useEffect, useRef, useState, type SyntheticEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import type { SimpleKeyboard } from "react-simple-keyboard";

import type { RootState } from "../../store";
import {
    backspaceVirtualKeyboardValue,
    findVirtualKeyboardTarget,
    focusNextVirtualKeyboardTarget,
    getVirtualKeyboardMode,
    insertVirtualKeyboardText,
    shouldKeepVirtualKeyboardOpen,
    syncVirtualKeyboardInput,
    VIRTUAL_KEYBOARD_KEEP_OPEN_ATTRIBUTE,
    type VirtualKeyboardMode,
    type VirtualKeyboardTarget,
} from "../../helpers/virtualKeyboard";
import CustomSimpleKeyboard from "../../components/CustomSimpleKeyboard";
import { CircleInfoIcon } from "../../components/svgIcons/AllIcons";
import { Overlay, Popover } from "react-bootstrap";
import { toggleShowSettingsModal } from "../../store/slices/appGeneralSlice";

import "./GlobalVirtualKeyboard.scss";


const getBaseLayoutName = (mode: VirtualKeyboardMode) => {
    if (mode === "email") return "emailMobile";
    if (mode === "tel") return "phone";
    if (mode === "number") return "numbersWithUpAndDownArrows";
    return "mobile";
};

const getShiftLayoutName = (mode: VirtualKeyboardMode) => {
    if (mode === "email") return "emailMobileShift";
    if (mode === "number") return "numbersWithUpAndDownArrows";
    return "mobileShift";
};

const normalizeNumberKeyboardValue = (value: string) => {
    let nextValue = (value || "").toString().replace(/[^0-9.-]+/g, '').replace(/(?!^)-/g, '');
    const firstDecimalSignIndex = nextValue.indexOf(".");
    nextValue = nextValue.replaceAll(".", "");

    if (firstDecimalSignIndex < 0) {
        return nextValue;
    }

    if (firstDecimalSignIndex !== nextValue.length) {
        return `${nextValue.slice(0, firstDecimalSignIndex)}.${nextValue.slice(firstDecimalSignIndex)}`;
    }

    return `${nextValue}.`;
};

const syncNumberKeyboardValue = (
    target: VirtualKeyboardTarget,
    nextValue: string,
    rawCaretPosition = nextValue.length,
) => {
    const normalizedValue = normalizeNumberKeyboardValue(nextValue);
    const normalizedCaretPosition = normalizeNumberKeyboardValue(nextValue.slice(0, rawCaretPosition)).length;

    syncVirtualKeyboardInput(
        target,
        normalizedValue,
        Math.min(normalizedCaretPosition, normalizedValue.length),
    );
};

const insertNumberKeyboardText = (
    target: VirtualKeyboardTarget,
    insertedText: string,
) => {
    const { value } = target;
    const selectionStart = target.selectionStart ?? value.length;
    const selectionEnd = target.selectionEnd ?? value.length;
    const nextValue =
        value.slice(0, selectionStart) +
        insertedText +
        value.slice(selectionEnd);

    syncNumberKeyboardValue(target, nextValue, selectionStart + insertedText.length);
};

const getScrollAnchor = (target: VirtualKeyboardTarget) => {
    if (target.id) {
        const explicitLabel = document.querySelector(`label[for="${target.id}"]`);
        if (explicitLabel instanceof HTMLElement) {
            return explicitLabel;
        }
    }

    const fieldContainer = target.closest(".text-start, .col-12, .col-md-6, .col-md-4, .col-lg-9, .col-lg-8, .col-lg-4, .col-lg-3, .mb-3, .mt-3");
    const nestedLabel = fieldContainer?.querySelector("label");
    if (nestedLabel instanceof HTMLElement) {
        return nestedLabel;
    }

    const previousLabel = target.previousElementSibling;
    if (previousLabel instanceof HTMLLabelElement) {
        return previousLabel;
    }

    return target;
};

const GlobalVirtualKeyboard = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const showKeyboard = useSelector((state: RootState) => state.appGeneral.showKeyboard);

    const keyboard = useRef<SimpleKeyboard | null>(null);
    const shellRef = useRef<HTMLDivElement | null>(null);
    const [activeTarget, setActiveTarget] = useState<VirtualKeyboardTarget | null>(null);
    const [activeTargetInputValue, setActiveTargetInputValue] = useState("");
    const [layoutName, setLayoutName] = useState("mobile");
    const [keyboardUseHintTooltipTargetElem, setKeyboardUseHintTooltipTargetElem] = useState<HTMLButtonElement | null>();

    const activeMode = activeTarget ? getVirtualKeyboardMode(activeTarget) : null;
    const clearButtonText = activeTarget?.dataset.virtualKeyboardClearLabel || t("clear");
    const clearButtonValue = activeTarget?.dataset.virtualKeyboardClearValue ?? "";
    const clearButtonIsDisabled = activeMode === "number" && (
        activeTarget?.dataset.virtualKeyboardClearDisabled === "true" ||
        activeTargetInputValue === clearButtonValue
    );
    const keyboardTheme = activeMode === "number"
        ? `hg-theme-default hg-layout-default global-virtual-keyboard-board global-number-keyboard ${clearButtonIsDisabled ? "clear-btn-is-disabled" : ""}`
        : "hg-theme-default hg-layout-default gray-keyboard global-virtual-keyboard-board";

    useEffect(() => {
        if (!showKeyboard) {
            setActiveTarget(null);
            setLayoutName("mobile");
            return;
        }

        const handleFocusIn = (event: FocusEvent) => {
            const nextTarget = findVirtualKeyboardTarget(event.target);
            if (nextTarget) {
                setActiveTarget(nextTarget);
                return;
            }

            if (shellRef.current?.contains(event.target as Node)) {
                return;
            }

             if (shouldKeepVirtualKeyboardOpen(event.target)) {
                return;
            }

            setActiveTarget(null);
        };

        const handlePointerDown = (event: PointerEvent) => {
            if (shellRef.current?.contains(event.target as Node)) {
                return;
            }

            if (findVirtualKeyboardTarget(event.target)) {
                return;
            }

            if (shouldKeepVirtualKeyboardOpen(event.target)) {
                return;
            }

            setActiveTarget(null);
        };

        document.addEventListener("focusin", handleFocusIn);
        document.addEventListener("pointerdown", handlePointerDown, true);

        return () => {
            document.removeEventListener("focusin", handleFocusIn);
            document.removeEventListener("pointerdown", handlePointerDown, true);
        };
    }, [showKeyboard]);

    useEffect(() => {
        if (!activeTarget || !showKeyboard) {
            keyboard.current?.setInput("");
            setActiveTargetInputValue("");
            return;
        }

        setLayoutName(getBaseLayoutName(getVirtualKeyboardMode(activeTarget)));

        const syncKeyboardInput = () => {
            const nextValue = activeTarget.value || "";
            keyboard.current?.setInput(nextValue);
            setActiveTargetInputValue(nextValue);
        };

        syncKeyboardInput();
        activeTarget.addEventListener("input", syncKeyboardInput);

        return () => {
            activeTarget.removeEventListener("input", syncKeyboardInput);
        };
    }, [activeTarget, showKeyboard]);

    const handleClose = () => {
        activeTarget?.blur();
        setActiveTarget(null);
    };

    useEffect(() => {
        const body = document.body;
        const root = document.documentElement;

        if (!showKeyboard || !activeTarget || !shellRef.current) {
            body.classList.remove("virtual-keyboard-open");
            root.style.removeProperty("--virtual-keyboard-height");
            return;
        }

        const keyboardHeight = shellRef.current.offsetHeight;
        const scrollAnchor = getScrollAnchor(activeTarget);
        activeTarget.style.scrollMarginBottom = `${keyboardHeight + 24}px`;
        scrollAnchor.style.scrollMarginTop = "16px";

        body.classList.add("virtual-keyboard-open");
        root.style.setProperty("--virtual-keyboard-height", `${keyboardHeight}px`);

        window.requestAnimationFrame(() => {
            scrollAnchor.scrollIntoView({
                block: "start",
                inline: "nearest",
                behavior: "smooth",
            });
        });

        return () => {
            activeTarget.style.removeProperty("scroll-margin-bottom");
            scrollAnchor.style.removeProperty("scroll-margin-top");
            body.classList.remove("virtual-keyboard-open");
            root.style.removeProperty("--virtual-keyboard-height");
        };
    }, [activeTarget, layoutName, showKeyboard]);

    const handleKeyPress = (key: string) => {
        if (!activeTarget || !activeMode) return;

        if (activeMode === "number") {
            if (key === "{arrowup}") {
                syncNumberKeyboardValue(activeTarget, `${Math.floor(Number(activeTarget.value) || 0) + 1}`);
                return;
            }

            if (key === "{arrowdown}") {
                syncNumberKeyboardValue(activeTarget, `${Math.ceil(Number(activeTarget.value) || 0) - 1}`);
                return;
            }

            if (key === "{clear}") {
                if (!clearButtonIsDisabled) {
                    syncNumberKeyboardValue(activeTarget, clearButtonValue);
                }
                return;
            }

            if (key === "{tab}") {
                focusNextVirtualKeyboardTarget(activeTarget);
                return;
            }

            if (key === "{backspace}") {
                backspaceVirtualKeyboardValue(activeTarget);
                return;
            }

            if (key === "{numpaddecimal}") {
                insertNumberKeyboardText(activeTarget, ".");
                return;
            }

            if (/^\d$/.test(key) || key === ".") {
                insertNumberKeyboardText(activeTarget, key);
                return;
            }

            if (key === "{enter}") {
                handleClose();
            }

            return;
        }

        if (key === "{shift}") {
            if (activeMode === "tel") return;
            setLayoutName((prevLayoutName) => (
                prevLayoutName === getShiftLayoutName(activeMode)
                    ? getBaseLayoutName(activeMode)
                    : getShiftLayoutName(activeMode)
            ));
            return;
        }

        if (key === "{numbers}") {
            setLayoutName("numbersComplete");
            return;
        }

        if (key === "{symbols}") {
            setLayoutName("symbols");
            return;
        }

        if (key === "{abc}" || key === "{mobileKeyboard}") {
            setLayoutName(getBaseLayoutName(activeMode));
            return;
        }

        if (key === "{backspace}") {
            backspaceVirtualKeyboardValue(activeTarget);
            return;
        }

        if (key === "{space}") {
            insertVirtualKeyboardText(activeTarget, " ");
            return;
        }

        if (key === "{tab}") {
            focusNextVirtualKeyboardTarget(activeTarget);
            return;
        }

        if (key === "{enter}") {
            if (activeTarget instanceof HTMLTextAreaElement) {
                insertVirtualKeyboardText(activeTarget, "\n");
                return;
            }

            handleClose();
            return;
        }

        if (key.startsWith("{")) {
            return;
        }

        insertVirtualKeyboardText(activeTarget, key);

        if (layoutName === "symbols" || layoutName === "numbersComplete") {
            setLayoutName(getBaseLayoutName(activeMode));
        } else if (layoutName === getShiftLayoutName(activeMode)) {
            setLayoutName(getBaseLayoutName(activeMode));
        }
    };

    const preventKeyboardBlur = (event: SyntheticEvent) => {
        event.preventDefault();
    };

    const handleShowInfo = (evt: MouseEvent<HTMLButtonElement>) => {
        if (keyboardUseHintTooltipTargetElem) {
            setKeyboardUseHintTooltipTargetElem(null);
            return;
        }

        setKeyboardUseHintTooltipTargetElem(evt?.currentTarget || evt?.target || null);
    };

    const handleOpenSettings = () => {
        setKeyboardUseHintTooltipTargetElem(null);
        activeTarget?.blur();
        setActiveTarget(null);
        dispatch(toggleShowSettingsModal({
            showModal: true,
        }));
    };

    if (!showKeyboard || !activeTarget) {
        return null;
    }

    return (
        <div
            ref={shellRef}
            className="global-virtual-keyboard-shell"
            onMouseDownCapture={preventKeyboardBlur}
            onTouchStartCapture={preventKeyboardBlur}
        >
            <div className="global-virtual-keyboard-header">
                <span className="global-virtual-keyboard-title">
                    {t("mobileKeyboard")}
                </span>
                <div className="global-virtual-keyboard-actions position-relative">
                    <button
                        type="button"
                        className="global-virtual-keyboard-info d-flex p-0"
                        aria-label={String(t("virtualKeyboardInfoLabel"))}
                        onClick={handleShowInfo}
                        {...{ [VIRTUAL_KEYBOARD_KEEP_OPEN_ATTRIBUTE]: "true" }}
                    >
                        <span className="icon info-alert-icon">
                            <CircleInfoIcon />
                        </span>
                    </button>
                    {keyboardUseHintTooltipTargetElem && (
                        <Overlay
                            show={!!keyboardUseHintTooltipTargetElem}
                            target={keyboardUseHintTooltipTargetElem}
                            placement="top"
                            container={document.body}
                            rootClose
                            onHide={() => setKeyboardUseHintTooltipTargetElem(null)}
                            popperConfig={{
                                strategy: "fixed",
                                modifiers: [
                                    {
                                        name: "preventOverflow",
                                        options: {
                                            rootBoundary: "viewport",
                                            padding: 10,
                                        },
                                    },
                                    {
                                        name: "flip",
                                        options: {
                                            fallbackPlacements: ["top", "bottom"],
                                        },
                                    },
                                ],
                            }}
                        >
                            <Popover id="virtual-keyboard-disable-hint" className="global-virtual-keyboard-info-popover fs-6">
                                <Popover.Body {...{ [VIRTUAL_KEYBOARD_KEEP_OPEN_ATTRIBUTE]: "true" }}>
                                    <Trans
                                        i18nKey="virtualKeyboardDisableHint"
                                        components={[
                                            <button
                                                key="open-settings"
                                                type="button"
                                                className="global-virtual-keyboard-inline-link text-orange"
                                                {...{ [VIRTUAL_KEYBOARD_KEEP_OPEN_ATTRIBUTE]: "true" }}
                                                onClick={handleOpenSettings}
                                            />,
                                        ]}
                                    />
                                </Popover.Body>
                            </Popover>
                        </Overlay>
                    )}
                    <button
                        type="button"
                        className="global-virtual-keyboard-close"
                        onClick={handleClose}
                    >
                        {t("close")}
                    </button>
                </div>
            </div>

            <CustomSimpleKeyboard
                keyboardRef={(keyboardRef) => {
                    keyboard.current = keyboardRef;
                }}
                layoutName={layoutName}
                theme={keyboardTheme}
                clearButtonText={clearButtonText}
                onKeyPress={handleKeyPress}
            />
        </div>
    );
};

export default GlobalVirtualKeyboard;
